import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getManagerPanel } from '../../services/migration/personnelDataService';

export interface ProductionUiPermissions {
  canView: boolean;
  canCreate: boolean;
  canComplete: boolean;
  canCancel: boolean;
  canReports: boolean;
  opsSetup: boolean;
  steps: boolean;
  planning: boolean;
  quality: boolean;
  /** "Sabit üretim yapabilir": serbest fiş açmadan yalnız tanımlı sabit üretimleri yapar. */
  fixed: boolean;
  loaded: boolean;
}

const NONE: ProductionUiPermissions = {
  canView: false,
  canCreate: false,
  canComplete: false,
  canCancel: false,
  canReports: false,
  opsSetup: false,
  steps: false,
  planning: false,
  quality: false,
  fixed: false,
  loaded: false,
};

/** Kural backend'deki ManagerPanelPermissions.IsGranted(production_*) ile aynıdır. */
export const resolveProductionPermissions = (settings: any): Omit<ProductionUiPermissions, 'loaded'> => {
  const source = settings || {};
  const subs = source.production_subPermissions || {};
  const open = source.production === true;
  return {
    canView: open,
    canCreate: open && subs.production_create === true,
    canComplete: open && subs.production_complete === true,
    canCancel: open && subs.production_cancel === true,
    canReports: open && subs.production_reports === true,
    opsSetup: open && subs.production_ops_setup === true,
    steps: open && subs.production_steps === true,
    planning: open && subs.production_planning === true,
    quality: open && subs.production_quality === true,
    fixed: open && subs.production_fixed === true,
  };
};

/**
 * Sabit üretimleri listeleyip üretebilir mi? Backend ProductionFixedOrderRules.CanUseFixedOrders ile aynı:
 * "Sabit üretim yapabilir" YA DA (taslak oluşturma VE tamamlama).
 */
export const canUseFixedOrders = (perms: Pick<ProductionUiPermissions, 'fixed' | 'canCreate' | 'canComplete'>): boolean =>
  perms.fixed || (perms.canCreate && perms.canComplete);

/** Şube yöneticisinin üretim yetkileri. `enabled` false ise istek atılmaz. */
export const useManagerProductionPermissions = (enabled: boolean): ProductionUiPermissions => {
  const { currentUser } = useAuth();
  const [perms, setPerms] = useState<ProductionUiPermissions>(NONE);

  useEffect(() => {
    if (!enabled || !currentUser?.uid) {
      setPerms(NONE);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const mp = await getManagerPanel(currentUser.uid);
        if (cancelled) return;
        setPerms({ ...resolveProductionPermissions(mp.found ? mp.settings : null), loaded: true });
      } catch (e) {
        console.warn('[useManagerProductionPermissions] managerPanel okunamadı:', e);
        if (!cancelled) setPerms({ ...NONE, loaded: true });
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, currentUser?.uid]);

  return perms;
};
